import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '@/api/services/client.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { getErrorMessage } from '@/api/errors/error-handler';
import type { ProjectFilters, CreateProjectRequest, UpdateProjectRequest } from '@/api/types';

export const useProjects = (filters?: ProjectFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.projects.list(filters),
    queryFn: () => clientService.getProjects(filters),
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error: any) => {
      // Don't retry on NOT_APPROVED errors
      if (error?.response?.data?.error?.code === 'NOT_APPROVED' || 
          error?.response?.data?.error?.status === 'pending') {
        return false;
      }
      return failureCount < 1; // Retry once for other errors
    },
    retryDelay: 1000, // Wait 1 second before retry
    enabled: options?.enabled !== false, // Only call if enabled is true (client only)
  });
};

export const useProject = (id: number) => {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => clientService.getProjectDetails(id),
    enabled: !!id,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateProjectRequest | FormData) => clientService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      toast({
        title: t('portfolio.projectAdded') || 'تم إضافة المشروع',
        description: t('portfolio.projectAddedDesc') || 'تم إضافة المشروع بنجاح',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.addFailed') || 'فشل الإضافة',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProjectRequest | FormData }) =>
      clientService.updateProject(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(variables.id),
      });
      toast({
        title: t('portfolio.projectUpdated') || 'تم تحديث المشروع',
        description: t('portfolio.projectUpdatedDesc') || 'تم تحديث المشروع بنجاح',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.updateFailed') || 'فشل التحديث',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => clientService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      toast({
        title: t('portfolio.projectDeleted') || 'تم حذف المشروع',
        description: t('portfolio.projectDeletedDesc') || 'تم حذف المشروع بنجاح',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.deleteFailed') || 'فشل الحذف',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};
